import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { NavLink, useNavigate } from "react-router-dom";
import { MdClose, MdInfoOutline, MdAdd, MdKeyboardArrowUp, MdKeyboardArrowDown } from "react-icons/md";
import { Bar } from "react-chartjs-2";
import { Chart as ChartJS, BarElement, CategoryScale, LinearScale, Tooltip, Legend } from "chart.js";
import { PageHeader } from "../../components/HeaderComponents";
import { BreadCrumbsGeneral } from "../../components/NavigationComponents";
import { AccountContext } from "../../contexts/AccountContexts";
import { ContentContext } from "../../contexts/ContentContext";
import { PermissionContext } from "../../contexts/PermissionContexts";
import { formatTimestamp } from "../../utils";
import {
    requestSaveWarningSettings,
    requestUpdateWarningStatus,
    requestUpdateWarningStatusBulk,
    requestWarningAnalysis,
    requestWarningAnalysisList,
    requestWarnings,
    requestWarningSettings,
    requestWarningSummary,
} from "../../requests/warning_requests";
import {
    WarningDetails,
    WarningIcon,
    WarningTypeBadge,
    ticketLink,
    useDevName,
    warningStatusStyle,
    warningStatuses,
    warningSummary,
    warningTypeStyle,
    warningTypes,
} from "../../components/WarningComponents";

const PAGE_SIZE = 25;

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

// Date input value (YYYY-MM-DD) -> unix seconds at the start/end of that day in Turkey time.
export const dayStart = (d) => (d ? Math.floor(new Date(`${d}T00:00:00+03:00`).getTime() / 1000) : null);
export const dayEnd = (d) => (d ? Math.floor(new Date(`${d}T23:59:59+03:00`).getTime() / 1000) : null);

export const WarningsPage = () => {
    const { t } = useTranslation("common");
    const { permissions, hasPermission } = useContext(PermissionContext);
    const navigator = useNavigate();
    const [tab, setTab] = useState("list");

    useEffect(() => {
        if (permissions.length > 0 && !hasPermission("warnings:view")) {
            navigator("/admin/dashboard");
        }
    }, [permissions]);

    if (!hasPermission("warnings:view")) {
        return <></>;
    }

    const breadCrumbs = [
        { link: "/admin/dashboard", name: t("Home") },
        { link: null, name: t("warning.page_title") },
    ];

    return (
        <main className="flex flex-col justify-start items-center w-full py-2 gap-5 pb-10 px-4 h-full">
            <PageHeader pageTitle={t("warning.page_title")} />
            <div className="flex w-full justify-between">
                <div className="flex flex-col flex-1">
                    <h1 className="text-xl font-semibold flex items-center gap-2"><WarningIcon size={20} /> {t("warning.page_title")}</h1>
                    <BreadCrumbsGeneral elements={breadCrumbs} />
                </div>
            </div>
            {hasPermission("warnings:settings") && (
                <div role="tablist" className="tabs tabs-boxed self-start bg-white dark:bg-gray-700">
                    <button type="button" role="tab" className={`tab ${tab === "list" ? "tab-active" : ""}`} onClick={() => setTab("list")}>{t("warning.tab_list")}</button>
                    <button type="button" role="tab" className={`tab ${tab === "settings" ? "tab-active" : ""}`} onClick={() => setTab("settings")}>{t("warning.tab_settings")}</button>
                </div>
            )}
            {tab === "list" ? <WarningsList /> : <WarningSettingsForm />}
        </main>
    );
};

// Cards above the list: today, this week (by type), still open, answered in the last 30 days, top employees.
// Durum için küçük renkli nokta (tabloda hızlı tarama).
const warningStatusDot = { NEW: "bg-red-500", REVIEWED: "bg-amber-500", ACTIONED: "bg-green-500", ANSWERED: "bg-sky-500" };

// Her kategoriye isminden türeyen sabit bir renk atanır; aynı kategori her satırda aynı renkte
// görünür, böylece tabloda göz hızlıca tarar. Renkler açık/koyu modda da okunur (açık rozet + koyu yazı).
// Tailwind purge'üne takılmamak için inline style kullanıyoruz (dinamik sınıf adı yerine).
const CATEGORY_COLORS = [
    ["#E6F1FB", "#0C447C"], ["#E1F5EE", "#0F6E56"], ["#FAECE7", "#712B13"], ["#EEEDFE", "#3C3489"],
    ["#FBEAF0", "#72243E"], ["#FAEEDA", "#633806"], ["#EAF3DE", "#27500A"], ["#F1EFE8", "#444441"],
];
export const categoryColor = (name) => {
    let h = 0;
    for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
    return CATEGORY_COLORS[h % CATEGORY_COLORS.length];
};

const SummaryCards = ({ refreshKey, onPickEmployee, onPickOpen, onOpenAnalysis }) => {
    const { t } = useTranslation("common");
    const [s, setS] = useState(null);

    useEffect(() => {
        requestWarningSummary({})
            .then(setS)
            .catch((error) => console.error(error.status, error.message));
    }, [refreshKey]);

    if (!s) return null;
    const card = "bg-white dark:bg-gray-700 rounded-xl shadow-sm p-3 flex flex-col gap-1 min-w-0";
    const big = "text-2xl font-semibold tabular-nums leading-tight";
    // Sıfır değerler soluk gösterilir ki dikkat gerçek sayılara gitsin.
    const num = (n) => `${big} ${n ? "" : "opacity-30"}`;
    const maxTop = Math.max(1, ...s.top_employees_30d.map((e) => e.count));
    // Card title with an info icon; hovering the icon shows what the number counts.
    const Title = ({ name, alignRight = false }) => (
        <span className="text-xs flex items-center gap-1">
            <span className="opacity-70">{t(`warning.summary.${name}`)}</span>
            <span className="relative group">
                <MdInfoOutline size={14} className="cursor-help opacity-70" />
                {/* opens toward the card, so it is not cut off by the side menu or the screen edge */}
                <span className={`hidden group-hover:block absolute ${alignRight ? "right-0" : "left-0"} top-full mt-1 z-50 w-56 rounded-lg bg-gray-900 text-white text-xs leading-snug p-2 shadow-lg`}>
                    {t(`warning.summary.info.${name}`)}
                </span>
            </span>
        </span>
    );

    return (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 w-full">
            <div className={card}>
                <Title name="today" />
                <span className={num(s.today)}>{s.today}</span>
            </div>
            <div className={card}>
                <Title name="this_week" />
                <span className={num(s.this_week)}>{s.this_week}</span>
                <div className="flex flex-wrap gap-1">
                    {Object.entries(s.week_by_type).filter(([, n]) => n > 0).map(([type, n]) => (
                        <span key={type} className={`badge badge-sm border-none text-white ${warningTypeStyle[type]}`}>{t(`warning.type.${type}`)}: {n}</span>
                    ))}
                </div>
            </div>
            {/* Kahraman kart: tıklanınca listeyi "Yeni"ye filtreler. */}
            <button
                type="button"
                onClick={() => onPickOpen && onPickOpen()}
                title={t("warning.summary.open_action")}
                className={`${card} text-left transition hover:shadow-md ring-1 ${s.open > 0 ? "ring-red-400/60 dark:ring-red-500/40 bg-red-50/60 dark:bg-red-950/30" : "ring-transparent"}`}
            >
                <Title name="open" />
                <span className={`text-3xl font-bold tabular-nums leading-tight ${s.open > 0 ? "text-red-600 dark:text-red-400" : "opacity-30"}`}>{s.open}</span>
                {s.open > 0 && <span className="text-[11px] text-red-600/80 dark:text-red-400/80">{t("warning.summary.open_action")}</span>}
            </button>
            <div className={card}>
                <Title name="answered" />
                <span className={num(s.answered_30d)}>{s.answered_30d}</span>
                {s.avg_answer_hours_30d !== null && <span className="text-xs opacity-70">{t("warning.summary.avg_answer", { hours: s.avg_answer_hours_30d })}</span>}
            </div>
            {/* Clicking the card opens the analysis window; a name still filters the list below. */}
            <div role="button" tabIndex={0} title={t("warning.analysis.open")} className={`${card} col-span-2 lg:col-span-1 cursor-pointer transition hover:shadow-md`}
                onClick={onOpenAnalysis} onKeyDown={(ev) => ev.key === "Enter" && onOpenAnalysis()}>
                <Title name="top_employees" alignRight />
                {s.top_employees_30d.length === 0 && <span className="text-sm italic opacity-70">-</span>}
                <div className="flex flex-col gap-1 mt-0.5">
                    {s.top_employees_30d.map((e) => (
                        <button key={e.employee_id} className="group/row flex items-center gap-2 text-sm text-left" onClick={(ev) => { ev.stopPropagation(); onPickEmployee(e.employee_id); }}>
                            <span className="w-14 shrink-0 truncate group-hover/row:underline">{e.name}</span>
                            <span className="flex-1 h-2 rounded-full bg-gray-200 dark:bg-gray-600 overflow-hidden">
                                <span className="block h-full rounded-full bg-orange-400 dark:bg-orange-500" style={{ width: `${(e.count / maxTop) * 100}%` }} />
                            </span>
                            <b className="w-6 text-right tabular-nums">{e.count}</b>
                        </button>
                    ))}
                </div>
                <span className="text-xs text-primary dark:text-blue-400 hover:underline self-start mt-1">{t("warning.analysis.open")} ›</span>
            </div>
        </div>
    );
};

// Same colors as the warning type badges.
const warningTypeColor = {
    response_delay: "#f97316",
    response_escalation: "#991b1b",
    auto_reply_close: "#9333ea",
    manual: "#dc2626",
    keyword: "#be123c",
};
const dateInputValue = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const WarningDate = ({ timestamp }) => {
    const [time, date] = formatTimestamp(timestamp * 1000).split(" ");
    return (
        <div className="flex flex-col leading-tight">
            <span>{time}</span>
            <span className="text-xs opacity-60">{date}</span>
        </div>
    );
};

// The "Most warnings" card in detail: every employee by warning type, for any types and dates.
const WarningAnalysisModal = ({ onClose }) => {
    const { t } = useTranslation("common");
    const { isDarkMode, openErrorModal } = useContext(ContentContext);
    const defaults = useMemo(() => {
        const now = new Date();
        return { from: dateInputValue(new Date(now.getTime() - 30 * 86400000)), to: dateInputValue(now) };
    }, []);
    const presets = useMemo(() => {
        const now = new Date();
        return [
            { key: "last_7", from: dateInputValue(new Date(now.getTime() - 6 * 86400000)), to: defaults.to },
            { key: "last_30", ...defaults },
            { key: "this_month", from: dateInputValue(new Date(now.getFullYear(), now.getMonth(), 1)), to: defaults.to },
            { key: "last_month", from: dateInputValue(new Date(now.getFullYear(), now.getMonth() - 1, 1)), to: dateInputValue(new Date(now.getFullYear(), now.getMonth(), 0)) },
        ];
    }, [defaults]);
    const [dates, setDates] = useState(defaults);
    const [types, setTypes] = useState(warningTypes);
    const [data, setData] = useState(null);
    const [sort, setSort] = useState({ key: "total", desc: true });
    const [employeeId, setEmployeeId] = useState(null);
    const [list, setList] = useState({ warnings: [], total: 0 });
    const [page, setPage] = useState(1);
    const boxRef = useRef(null);

    // Untouched dates are sent as none: the last 30 days counted like the card.
    const range = dates.from === defaults.from && dates.to === defaults.to ? {} : { dateFrom: dayStart(dates.from), dateTo: dayEnd(dates.to) };
    const showError = (error) => {
        console.error(error.status, error.message);
        openErrorModal(error);
    };

    useEffect(() => {
        requestWarningAnalysis({ types, ...range }).then(setData).catch(showError);
    }, [types, dates]);

    useEffect(() => {
        if (!employeeId) {
            setList({ warnings: [], total: 0 });
            return;
        }
        requestWarningAnalysisList({ employeeId, types, ...range, page, pageSize: PAGE_SIZE }).then(setList).catch(showError);
    }, [employeeId, types, dates, page]);

    // At least one type stays selected.
    const toggleType = (type) => {
        const next = warningTypes.filter((x) => (x === type ? !types.includes(x) : types.includes(x)));
        if (next.length === 0) return;
        setPage(1);
        setTypes(next);
    };
    const selectAllTypes = () => {
        setPage(1);
        setTypes(warningTypes);
    };
    const setDate = (key, value) => {
        setPage(1);
        setDates((prev) => ({ ...prev, [key]: value || defaults[key] }));
    };
    // Picking an employee switches the window to their warnings; "Back" returns to the table.
    const pickEmployee = (id) => {
        document.activeElement?.blur(); // closes the type filter if it is open
        setEmployeeId(id);
        setPage(1);
        if (boxRef.current) boxRef.current.scrollTop = 0;
    };

    const employees = data?.employees || [];
    const value = (e, key) => (key === "name" ? e.name.toLowerCase() : key === "total" ? e.total : e.by_type[key] || 0);
    const rows = [...employees].sort((a, b) => {
        const x = value(a, sort.key), y = value(b, sort.key);
        const order = x < y ? -1 : x > y ? 1 : a.name.localeCompare(b.name);
        return sort.desc && x !== y ? -order : order;
    });
    const sortBy = (key) => setSort((prev) => ({ key, desc: prev.key === key ? !prev.desc : key !== "name" }));
    const sum = (key) => employees.reduce((n, e) => n + value(e, key), 0);

    // Employees come sorted by total, so the chart shows the ten with the most warnings.
    const top = employees.filter((e) => e.total > 0).slice(0, 10);
    const textColor = isDarkMode ? "#e5e7eb" : "#374151";
    const gridColor = isDarkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)";
    const chartData = {
        labels: top.map((e) => e.name),
        datasets: types.map((type) => ({
            label: t(`warning.type.${type}`),
            data: top.map((e) => e.by_type[type] || 0),
            backgroundColor: warningTypeColor[type],
        })),
    };
    // No legend: the type buttons above have the same colors. Hovering a bar lists its types.
    const chartOptions = {
        indexAxis: "y",
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", axis: "y", intersect: false },
        // centerText: the doughnut's "TOTAL" label, registered for every chart on the user detail page
        plugins: { legend: { display: false }, tooltip: { filter: (item) => item.raw > 0 }, centerText: false },
        scales: {
            x: { stacked: true, ticks: { precision: 0, color: textColor }, grid: { color: gridColor } },
            y: { stacked: true, ticks: { color: textColor }, grid: { display: false } },
        },
        onClick: (event, elements) => elements.length > 0 && pickEmployee(top[elements[0].index].employee_id),
        onHover: (event, elements) => { event.native.target.style.cursor = elements.length > 0 ? "pointer" : "default"; },
    };


    // What the employee did, in a few words (the list's "What happened" column and the Excel file).
    const hours = (h) => (h >= 10 ? Math.round(h) : Math.round(h * 10) / 10);
    const whatHappened = (w) => {
        const d = w.details || {};
        switch (w.type) {
            case "response_delay":
                return t("warning.analysis.what.response_delay", { hours: hours(d.waiting_hours) });
            case "response_escalation":
                return t("warning.analysis.what.response_escalation", { hours: hours(d.waiting_hours), level: d.level_hours });
            case "auto_reply_close": {
                const [a, b] = d.tickets || [];
                return t("warning.analysis.what.auto_reply_close", { first: a?.created_at, second: b?.created_at });
            }
            case "manual":
                return t("warning.analysis.what.manual", { reason: d.reason, user: d.created_by_name });
            case "keyword":
                return t("warning.analysis.what.keyword", { keyword: d.keyword });
            default:
                return "";
        }
    };
    const warningResult = (w) => {
        const d = w.details || {};
        if (d.answered_at) return t(d.answer_kind === "closed" ? "warning.analysis.closed_after" : "warning.analysis.answered_after", { hours: hours(d.answered_after_hours) });
        return t(`warning.status.${w.status}`);
    };

    // A CSV file of what is on screen (the table, or the picked employee's warnings);
    // Excel opens it (";" and a BOM for Turkish Excel).
    const downloadCsv = (name, header, lines) => {
        // text starting with = + - @ would be read by Excel as a formula
        const cell = (v) => {
            const s = typeof v === "string" && /^[=+\-@]/.test(v) ? `'${v}` : String(v ?? "");
            return /[";\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        };
        const csv = "﻿" + [header, ...lines].map((line) => line.map(cell).join(";")).join("\r\n");
        const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
        const link = document.createElement("a");
        link.href = url;
        link.download = `${name}_${dates.from}_${dates.to}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    };
    const exportCsv = async () => {
        if (!selected) {
            const header = [t("warning.analysis.employee"), ...types.map((type) => t(`warning.type.${type}`)), t("warning.analysis.total")];
            const lines = rows.map((e) => [e.name, ...types.map((type) => e.by_type[type] || 0), e.total]);
            downloadCsv("uyari-analizi", header, lines);
            return;
        }
        try {
            const warnings = [];
            for (let p = 1; ; p++) {
                const d = await requestWarningAnalysisList({ employeeId, types, ...range, page: p, pageSize: 200 });
                warnings.push(...d.warnings);
                if (d.warnings.length === 0 || warnings.length >= d.total) break;
            }
            const header = [t("warning.col_date"), t("warning.col_type"), t("warning.col_ticket"), t("warning.col_category"), t("warning.analysis.what_happened"), t("warning.col_status")];
            const lines = warnings.map((w) => [
                formatTimestamp(w.timestamp * 1000), t(`warning.type.${w.type}`), w.ticket_created_at ? `#${w.ticket_created_at}` : "-",
                w.ticket_category || "-", whatHappened(w), warningResult(w),
            ]);
            downloadCsv(`uyarilar_${selected.name}`, header, lines);
        } catch (error) {
            showError(error);
        }
    };

    const selectClass = "select select-sm select-bordered bg-white dark:bg-gray-700";
    const presetKey = presets.find((p) => p.from === dates.from && p.to === dates.to)?.key || "custom";
    const pickPreset = (key) => {
        const p = presets.find((x) => x.key === key);
        if (!p) return;
        setPage(1);
        setDates({ from: p.from, to: p.to });
    };
    const typeLabel = types.length === warningTypes.length ? t("All")
        : types.length === 1 ? t(`warning.type.${types[0]}`)
        : t("warning.analysis.n_types", { count: types.length });

    const selected = employees.find((e) => e.employee_id === employeeId);
    const nPages = Math.max(1, Math.ceil(list.total / PAGE_SIZE));
    const SortHeader = ({ name, label, className = "" }) => (
        <th className={`cursor-pointer select-none whitespace-nowrap hover:underline ${className}`} onClick={() => sortBy(name)}>
            {label}{sort.key === name && (sort.desc ? <MdKeyboardArrowDown className="inline" /> : <MdKeyboardArrowUp className="inline" />)}
        </th>
    );

    return createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40" onMouseDown={onClose}>
            <div ref={boxRef} className="bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 rounded-lg shadow-xl w-[96vw] max-w-6xl min-h-[70vh] max-h-[92vh] overflow-y-auto p-5 flex flex-col gap-4" onMouseDown={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-lg flex items-center gap-2"><WarningIcon size={18} /> {t("warning.analysis.title")}</h3>
                    <button className="btn btn-sm btn-ghost" onClick={onClose}><MdClose /></button>
                </div>

                <section className="flex flex-col gap-2 rounded-lg bg-gray-50 dark:bg-gray-900/40 p-3">
                    <div className="flex flex-wrap items-end gap-3">
                        <label className="flex flex-col text-xs gap-1">
                            {t("warning.analysis.period")}
                            <select className={selectClass} value={presetKey} onChange={(e) => pickPreset(e.target.value)}>
                                {presets.map((p) => <option key={p.key} value={p.key}>{t(`warning.analysis.${p.key}`)}</option>)}
                                {presetKey === "custom" && <option value="custom">{t("warning.analysis.custom")}</option>}
                            </select>
                        </label>
                        <label className="flex flex-col text-xs gap-1">
                            {t("warning.analysis.date_from")}
                            <input type="date" className="input input-sm input-bordered bg-white dark:bg-gray-700" value={dates.from} max={dates.to} onChange={(e) => setDate("from", e.target.value)} />
                        </label>
                        <label className="flex flex-col text-xs gap-1">
                            {t("warning.analysis.date_to")}
                            <input type="date" className="input input-sm input-bordered bg-white dark:bg-gray-700" value={dates.to} min={dates.from} onChange={(e) => setDate("to", e.target.value)} />
                        </label>
                        <div className="flex flex-col text-xs gap-1">
                            {t("warning.filter_type")}
                            <div className="dropdown">
                                <div tabIndex={0} role="button" className={`${selectClass} flex items-center min-w-52`}>{typeLabel}</div>
                                <ul tabIndex={0} className="dropdown-content z-[110] menu menu-sm p-1 mt-1 shadow-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-box w-64">
                                    <li>
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input type="checkbox" className="checkbox checkbox-xs" checked={types.length === warningTypes.length}
                                                onChange={selectAllTypes} />
                                            {t("All")}
                                        </label>
                                    </li>
                                    {warningTypes.map((type) => (
                                        <li key={type}>
                                            <label className="flex items-center gap-2 cursor-pointer">
                                                <input type="checkbox" className="checkbox checkbox-xs" checked={types.includes(type)} onChange={() => toggleType(type)} />
                                                {t(`warning.type.${type}`)}
                                            </label>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                        <button className="btn btn-sm ml-auto" onClick={exportCsv} disabled={!data || (selected ? list.total === 0 : rows.length === 0)}>{t("warning.analysis.export")}</button>
                    </div>
                    {types.includes("keyword") && <span className="text-xs opacity-70">{t("warning.analysis.keyword_note")}</span>}
                </section>

                {data === null ? (
                    <p className="text-sm italic opacity-70">…</p>
                ) : !selected ? (
                    <section className="flex flex-col gap-3">
                        <span className="text-sm font-semibold">{t("warning.analysis.employees")}</span>
                        {top.length > 0 ? (
                            <div className="flex flex-col gap-1">
                                <span className="text-xs opacity-70">{t("warning.analysis.chart_title")}</span>
                                <div style={{ height: 30 + top.length * 24 }}>
                                    <Bar data={chartData} options={chartOptions} />
                                </div>
                            </div>
                        ) : (
                            <p className="text-sm italic opacity-70">{t("warning.analysis.no_warnings")}</p>
                        )}

                        <div className="flex flex-col gap-1">
                            <span className="text-xs opacity-70">
                                {t("warning.analysis.sort_hint")}
                            </span>
                            <div className="overflow-x-auto max-h-96 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                                <table className="table table-sm table-pin-rows w-full">
                                    <thead>
                                        <tr className="bg-gray-50 dark:bg-gray-700">
                                            <SortHeader name="name" label={t("warning.analysis.employee")} />
                                            {types.map((type) => <SortHeader key={type} name={type} label={t(`warning.type.${type}`)} className="text-right" />)}
                                            <SortHeader name="total" label={t("warning.analysis.total")} className="text-right" />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((e) => (
                                            <tr key={e.employee_id} onClick={() => pickEmployee(e.employee_id)} className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                                <td className="whitespace-nowrap">{e.name}</td>
                                                {types.map((type) => (
                                                    <td key={type} className={`text-right tabular-nums ${e.by_type[type] ? "" : "opacity-30"}`}>{e.by_type[type] || 0}</td>
                                                ))}
                                                <td className={`text-right tabular-nums font-semibold ${e.total ? "" : "opacity-30"}`}>{e.total}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <tr className="bg-gray-50 dark:bg-gray-700 text-sm">
                                            <td>{t("warning.analysis.total")}</td>
                                            {types.map((type) => <td key={type} className="text-right tabular-nums">{sum(type)}</td>)}
                                            <td className="text-right tabular-nums">{sum("total")}</td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                            {data.unassigned > 0 && <span className="text-xs opacity-70">{t("warning.analysis.unassigned", { count: data.unassigned })}</span>}
                        </div>
                    </section>
                ) : (
                    <section className="flex flex-col gap-3">
                        <div className="flex flex-wrap items-center gap-3">
                            <button className="btn btn-sm" onClick={() => pickEmployee(null)}>‹ {t("warning.analysis.back")}</button>
                            <span className="text-sm font-semibold">{t("warning.analysis.employee_warnings", { name: selected.name, count: list.total })}</span>
                        </div>
                        <span className="text-sm opacity-80">
                            {types.filter((type) => selected.by_type[type]).map((type) => `${t(`warning.type.${type}`)}: ${selected.by_type[type]}`).join(" · ")}
                        </span>
                        <div className="overflow-x-auto">
                            <table className="table table-sm w-full">
                                <thead>
                                    <tr>
                                        <th>{t("warning.col_date")}</th>
                                        <th>{t("warning.col_type")}</th>
                                        <th>{t("warning.col_ticket")}</th>
                                        <th>{t("warning.col_category")}</th>
                                        <th>{t("warning.analysis.what_happened")}</th>
                                        <th>{t("warning.col_status")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {list.warnings.map((w) => (
                                        <tr key={w.id}>
                                            <td className="whitespace-nowrap"><WarningDate timestamp={w.timestamp} /></td>
                                            <td className="whitespace-nowrap">{t(`warning.type.${w.type}`)}</td>
                                            <td className="whitespace-nowrap">
                                                {w.ticket_code ? (
                                                    <NavLink className="text-primary dark:text-blue-400 underline" to={ticketLink(w.ticket_code)}>#{w.ticket_created_at}</NavLink>
                                                ) : "-"}
                                            </td>
                                            <td className="whitespace-nowrap">{w.ticket_category || "-"}</td>
                                            <td>{whatHappened(w)}</td>
                                            <td className="whitespace-nowrap">{warningResult(w)}</td>
                                        </tr>
                                    ))}
                                    {list.warnings.length === 0 && (
                                        <tr><td colSpan={6} className="text-center italic opacity-70 py-4">{t("warning.empty")}</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                        {list.total > PAGE_SIZE && (
                            <div className="flex items-center justify-center gap-3">
                                <button className="btn btn-sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>‹</button>
                                <span className="text-sm">{page} / {nPages}</span>
                                <button className="btn btn-sm" disabled={page >= nPages} onClick={() => setPage(page + 1)}>›</button>
                            </div>
                        )}
                    </section>
                )}
            </div>
        </div>,
        document.body
    );
};

// Asks for an optional note before marking one or more warnings.
const StatusNoteModal = ({ count, status, templates = [], onConfirm, onClose }) => {
    const { t } = useTranslation("common");
    const [note, setNote] = useState("");
    const insertTemplate = (tpl) => {
        setNote((prev) => (prev.trim() ? `${prev.trim()}\n${tpl}` : tpl));
        document.activeElement?.blur(); // dropdown'ı kapat
    };
    return createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40">
            <div className="bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 rounded-lg shadow-xl w-[92vw] max-w-md p-5 flex flex-col gap-3">
                <div className="flex items-center justify-between">
                    <h3 className="font-semibold">{t("warning.note_title", { count, status: t(`warning.status.${status}`) })}</h3>
                    <button className="btn btn-sm btn-ghost" onClick={onClose}><MdClose /></button>
                </div>
                <div className="flex items-center justify-between gap-2">
                    <label className="text-sm font-medium" htmlFor="warning-note">{t("warning.note_label")}</label>
                    {templates.length > 0 && (
                        <div className="dropdown dropdown-end">
                            <div tabIndex={0} role="button" className="btn btn-xs btn-ghost gap-1 normal-case">
                                <MdAdd /> {t("warning.add_template")}
                            </div>
                            <ul tabIndex={0} className="dropdown-content z-[110] menu menu-sm p-1 shadow-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-box w-72 max-h-56 overflow-y-auto flex-nowrap">
                                {templates.map((tpl, i) => (
                                    <li key={i}>
                                        <button type="button" className="text-left whitespace-normal" onClick={() => insertTemplate(tpl)}>{tpl}</button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
                <textarea
                    id="warning-note"
                    className="textarea textarea-bordered w-full h-24 bg-inherit"
                    value={note}
                    maxLength={1000}
                    placeholder={t("warning.note_placeholder")}
                    onChange={(e) => setNote(e.target.value)}
                    autoFocus
                />
                <div className="flex justify-end gap-2">
                    <button className="btn btn-sm btn-ghost" onClick={onClose}>{t("warning.cancel")}</button>
                    <button className="btn btn-sm bg-primary text-white border-none hover:bg-sky-700" onClick={() => onConfirm(note.trim())}>{t("warning.save")}</button>
                </div>
            </div>
        </div>,
        document.body
    );
};

const WarningsList = () => {
    const { t } = useTranslation("common");
    const { devs } = useContext(AccountContext);
    const { openErrorModal } = useContext(ContentContext);
    const { hasPermission } = useContext(PermissionContext);
    const devName = useDevName();
    const canManage = hasPermission("warnings:manage");

    // Liste varsayılan olarak yalnızca "Yeni" (işlenmemiş) uyarılarla açılır; "Tümü" ile hepsi görünür.
    const [filters, setFilters] = useState({ type: "", employeeId: "", status: "NEW", dateFrom: "", dateTo: "", q: "" });
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const [data, setData] = useState({ warnings: [], total: 0, n_new: 0 });
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState([]);
    const [pending, setPending] = useState(null); // {ids, status} waiting for the note dialog
    const [refreshKey, setRefreshKey] = useState(0);
    const [showAnalysis, setShowAnalysis] = useState(false);

    const load = () => {
        setLoading(true);
        requestWarnings({
            type: filters.type,
            employeeId: filters.employeeId,
            status: filters.status,
            dateFrom: dayStart(filters.dateFrom),
            dateTo: dayEnd(filters.dateTo),
            q: filters.q,
            page,
            pageSize: PAGE_SIZE,
        })
            .then((d) => {
                setData(d);
                setSelected((prev) => prev.filter((id) => d.warnings.some((w) => w.id === id)));
            })
            .catch((error) => {
                console.error(error.status, error.message);
                openErrorModal(error);
            })
            .finally(() => setLoading(false));
    };

    useEffect(load, [filters, page]);

    // Search as you type, without a request per keystroke.
    useEffect(() => {
        const timer = setTimeout(() => {
            if (search.trim() !== filters.q) setFilter("q", search.trim());
        }, 400);
        return () => clearTimeout(timer);
    }, [search]);

    const setFilter = (key, value) => {
        setPage(1);
        setFilters((prev) => ({ ...prev, [key]: value }));
    };

    const applyStatus = ({ ids, status }, note = "") => {
        const request = ids.length === 1
            ? requestUpdateWarningStatus({ id: ids[0], status, note })
            : requestUpdateWarningStatusBulk({ ids, status, note });
        request
            .then(() => {
                setSelected([]);
                setRefreshKey((k) => k + 1);
                load();
            })
            .catch((error) => {
                console.error(error.status, error.message);
                openErrorModal(error);
            });
    };

    // "Back to new" needs no note; marking reviewed/actioned asks for one (optional).
    const askStatus = (ids, status) => (status === "NEW" ? applyStatus({ ids, status }) : setPending({ ids, status }));

    const employees = useMemo(
        () => Object.values(devs || {}).sort((a, b) => (a.name || "").localeCompare(b.name || "")),
        [devs]
    );
    const nPages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));
    const selectClass = "select select-sm select-bordered bg-white dark:bg-gray-700";
    const pageIds = data.warnings.map((w) => w.id);
    const allOnPage = pageIds.length > 0 && pageIds.every((id) => selected.includes(id));
    const toggle = (id) => setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

    return (
        <>
            <SummaryCards refreshKey={refreshKey} onPickEmployee={(id) => setFilter("employeeId", id)} onPickOpen={() => setFilter("status", "NEW")} onOpenAnalysis={() => setShowAnalysis(true)} />
            {showAnalysis && <WarningAnalysisModal onClose={() => setShowAnalysis(false)} />}
            <div className="flex flex-col w-full bg-white dark:bg-gray-700 rounded-xl shadow-lg py-4 gap-3">
                <div className="flex flex-wrap items-end gap-3 px-4">
                    <label className="flex flex-col text-xs gap-1 grow min-w-48 max-w-sm">
                        {t("warning.search")}
                        <input
                            type="search"
                            className="input input-sm input-bordered bg-white dark:bg-gray-700"
                            value={search}
                            placeholder={t("warning.search_placeholder")}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </label>
                    <label className="flex flex-col text-xs gap-1">
                        {t("warning.filter_type")}
                        <select className={selectClass} value={filters.type} onChange={(e) => setFilter("type", e.target.value)}>
                            <option value="">{t("All")}</option>
                            {warningTypes.map((type) => <option key={type} value={type}>{t(`warning.type.${type}`)}</option>)}
                        </select>
                    </label>
                    <label className="flex flex-col text-xs gap-1">
                        {t("warning.filter_employee")}
                        <select className={selectClass} value={filters.employeeId} onChange={(e) => setFilter("employeeId", e.target.value)}>
                            <option value="">{t("All")}</option>
                            {employees.map((dev) => <option key={dev.id} value={dev.id}>{dev.name}</option>)}
                        </select>
                    </label>
                    <label className="flex flex-col text-xs gap-1">
                        {t("warning.filter_status")}
                        <select className={selectClass} value={filters.status} onChange={(e) => setFilter("status", e.target.value)}>
                            <option value="">{t("All")}</option>
                            {warningStatuses.map((s) => <option key={s} value={s}>{t(`warning.status.${s}`)}</option>)}
                        </select>
                    </label>
                    <label className="flex flex-col text-xs gap-1">
                        {t("warning.filter_from")}
                        <input type="date" className="input input-sm input-bordered bg-white dark:bg-gray-700" value={filters.dateFrom} onChange={(e) => setFilter("dateFrom", e.target.value)} />
                    </label>
                    <label className="flex flex-col text-xs gap-1">
                        {t("warning.filter_to")}
                        <input type="date" className="input input-sm input-bordered bg-white dark:bg-gray-700" value={filters.dateTo} onChange={(e) => setFilter("dateTo", e.target.value)} />
                    </label>
                    <button className="btn btn-sm" onClick={() => { load(); setRefreshKey((k) => k + 1); }} disabled={loading}>{t("warning.refresh")}</button>
                </div>

                {canManage && selected.length > 0 && (
                    <div className="flex flex-wrap items-center gap-2 mx-4 px-3 py-2 rounded-lg bg-sky-50 dark:bg-sky-900/40 text-sm">
                        <b>{t("warning.bulk_selected", { count: selected.length })}</b>
                        <button className="btn btn-xs" onClick={() => askStatus(selected, "REVIEWED")}>{t("warning.mark_reviewed")}</button>
                        <button className="btn btn-xs btn-success text-white" onClick={() => askStatus(selected, "ACTIONED")}>{t("warning.mark_actioned")}</button>
                        <button className="btn btn-xs btn-ghost" onClick={() => askStatus(selected, "NEW")}>{t("warning.mark_new")}</button>
                        <button className="btn btn-xs btn-ghost" onClick={() => setSelected([])}>{t("warning.bulk_clear")}</button>
                    </div>
                )}

                <div className="overflow-x-auto w-full">
                    <table className="table table-sm w-full">
                        <thead>
                            <tr>
                                {canManage && (
                                    <th>
                                        <input
                                            type="checkbox"
                                            className="checkbox checkbox-xs"
                                            aria-label={t("warning.select_page")}
                                            checked={allOnPage}
                                            onChange={() => setSelected(allOnPage ? selected.filter((id) => !pageIds.includes(id)) : [...new Set([...selected, ...pageIds])])}
                                        />
                                    </th>
                                )}
                                <th>{t("warning.col_date")}</th>
                                <th>{t("warning.col_type")}</th>
                                <th>{t("warning.col_employee")}</th>
                                <th>{t("warning.col_ticket")}</th>
                                <th>{t("warning.col_category")}</th>
                                <th className="min-w-72">{t("warning.col_detail")}</th>
                                <th>{t("warning.col_status")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.warnings.map((w) => (
                                <tr key={w.id} className={`transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/40 ${w.status === "NEW" ? "bg-red-50/30 dark:bg-red-950/15 [&>td:first-child]:border-l-4 [&>td:first-child]:border-red-500" : ""}`}>
                                    {canManage && (
                                        <td>
                                            <input type="checkbox" className="checkbox checkbox-xs" aria-label={t("warning.select_row")} checked={selected.includes(w.id)} onChange={() => toggle(w.id)} />
                                        </td>
                                    )}
                                    <td className="whitespace-nowrap">
                                        {(() => {
                                            const [time, date] = formatTimestamp(w.timestamp * 1000).split(" ");
                                            return (
                                                <div className="flex flex-col leading-tight">
                                                    <span>{time}</span>
                                                    <span className="text-xs opacity-60">{date}</span>
                                                </div>
                                            );
                                        })()}
                                    </td>
                                    <td><WarningTypeBadge type={w.type} /></td>
                                    <td className="whitespace-nowrap">{w.employee_id ? devName(w.employee_id) : "-"}</td>
                                    <td className="whitespace-nowrap">
                                        {w.ticket_code ? (
                                            <NavLink className="text-primary dark:text-blue-400 underline" to={ticketLink(w.ticket_code)}>#{w.ticket_created_at}</NavLink>
                                        ) : "-"}
                                    </td>
                                    <td className="whitespace-nowrap">
                                        {w.ticket_category
                                            ? <span className="badge border-none font-medium whitespace-nowrap" style={{ backgroundColor: categoryColor(w.ticket_category)[0], color: categoryColor(w.ticket_category)[1] }}>{w.ticket_category}</span>
                                            : <span className="opacity-40">-</span>}
                                    </td>
                                    <td>
                                        <div className="flex flex-col gap-1">
                                            <span className="font-medium text-gray-800 dark:text-gray-100">{warningSummary(t, w)}</span>
                                            <WarningDetails warning={w} />
                                            {w.review_note && <span className="text-xs opacity-70">{t("warning.review_note")}: {w.review_note}</span>}
                                        </div>
                                    </td>
                                    <td>
                                        <div className="flex flex-col items-start gap-1 min-w-32">
                                            {canManage ? (
                                                <div className="flex items-center gap-1.5">
                                                <span className={`inline-block w-2 h-2 rounded-full shrink-0 ${warningStatusDot[w.status] || "bg-gray-400"}`} />
                                                <select
                                                    className={`select select-xs select-bordered font-semibold ${warningStatusStyle[w.status]?.replace("badge-", "select-") || ""}`}
                                                    value={w.status}
                                                    onChange={(e) => askStatus([w.id], e.target.value)}
                                                    aria-label={t("warning.col_status")}
                                                >
                                                    {!["NEW", "REVIEWED", "ACTIONED"].includes(w.status) && (
                                                        <option value={w.status}>{t(`warning.status.${w.status}`)}</option>
                                                    )}
                                                    <option value="NEW">{t("warning.status.NEW")}</option>
                                                    <option value="REVIEWED">{t("warning.status.REVIEWED")}</option>
                                                    <option value="ACTIONED">{t("warning.status.ACTIONED")}</option>
                                                </select>
                                                </div>
                                            ) : (
                                                <span className={`badge whitespace-nowrap ${warningStatusStyle[w.status]}`}>{t(`warning.status.${w.status}`)}</span>
                                            )}
                                            {w.reviewed_by && <span className="text-[11px] opacity-70">{devName(w.reviewed_by)}</span>}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {data.warnings.length === 0 && (
                                <tr><td colSpan={canManage ? 8 : 7} className="text-center italic opacity-70 py-6">{loading ? "…" : t("warning.empty")}</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="flex items-center justify-center gap-3 px-4">
                    <button className="btn btn-sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>‹</button>
                    <span className="text-sm">{page} / {nPages} · {t("warning.total", { count: data.total })}</span>
                    <button className="btn btn-sm" disabled={page >= nPages} onClick={() => setPage(page + 1)}>›</button>
                </div>
            </div>
            {pending && (
                <StatusNoteModal
                    count={pending.ids.length}
                    status={pending.status}
                    templates={data.note_templates || []}
                    onClose={() => setPending(null)}
                    onConfirm={(note) => { applyStatus(pending, note); setPending(null); }}
                />
            )}
        </>
    );
};

const UserChecklist = ({ users, selected, onChange }) => (
    <div className="flex flex-wrap gap-2">
        {users.map((u) => (
            <label key={u.id} className={`flex items-center gap-2 border rounded-md px-2 py-1 text-sm cursor-pointer ${selected.includes(u.id) ? "border-primary bg-sky-50 dark:bg-sky-900" : "border-gray-300 dark:border-gray-600"}`}>
                <input
                    type="checkbox"
                    className="checkbox checkbox-xs"
                    checked={selected.includes(u.id)}
                    onChange={(e) => onChange(e.target.checked ? [...selected, u.id] : selected.filter((id) => id !== u.id))}
                />
                {u.name} <span className="opacity-60 text-xs">({u.rank})</span>
            </label>
        ))}
    </div>
);

const Toggle = ({ id, checked, onChange, label }) => (
    <label htmlFor={id} className="flex items-center gap-2 text-sm font-medium cursor-pointer w-fit">
        <input id={id} type="checkbox" className="toggle toggle-sm toggle-primary" checked={checked} onChange={(e) => onChange(e.target.checked)} />
        {label}
    </label>
);

const WarningSettingsForm = () => {
    const { t } = useTranslation("common");
    const { devs } = useContext(AccountContext);
    const { openErrorModal } = useContext(ContentContext);
    const [form, setForm] = useState(null);
    const [override, setOverride] = useState(null); // test-only server value that replaces the limit
    const [newKeyword, setNewKeyword] = useState("");
    const [newTemplate, setNewTemplate] = useState("");
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        requestWarningSettings({})
            .then((data) => {
                setForm(data.settings);
                setOverride(data.response_hours_overridden ? data.response_hours_override : null);
            })
            .catch((error) => {
                console.error(error.status, error.message);
                openErrorModal(error);
            });
    }, []);

    const users = useMemo(
        () => Object.values(devs || {}).filter((d) => d.type !== "deleted").sort((a, b) => (a.name || "").localeCompare(b.name || "")),
        [devs]
    );

    if (!form) return null;

    const update = (key, value) => {
        setSaved(false);
        setError(null);
        setForm((prev) => ({ ...prev, [key]: value }));
    };

    // Alanlar arası kurallar: ön uyarı < yanıt süresi < kademeler ≤ 720.
    const validate = () => {
        const resp = Number(form.response_hours);
        if (!(resp >= 1 && resp <= 720)) return t("warning.err_response_range");
        if (form.reminder_enabled) {
            const rem = Number(form.reminder_hours);
            if (!(rem >= 1 && rem < resp)) return t("warning.err_reminder", { limit: resp });
        }
        if (form.escalation_enabled) {
            const levels = form.escalation_hours.map(Number).filter((h) => h);
            if (!levels.length || levels.some((h) => h <= resp || h > 720)) {
                return t("warning.err_escalation", { limit: resp, steps: form.escalation_hours.filter((h) => h).join(", ") });
            }
        }
        return null;
    };

    const addKeyword = () => {
        const kw = newKeyword.trim();
        if (kw && !form.keywords.some((k) => k.toLocaleLowerCase("tr") === kw.toLocaleLowerCase("tr"))) {
            update("keywords", [...form.keywords, kw]);
        }
        setNewKeyword("");
    };

    const addTemplate = () => {
        const tpl = newTemplate.trim().replace(/\s+/g, " ");
        const list = form.note_templates || [];
        if (tpl && !list.some((x) => x.toLocaleLowerCase("tr") === tpl.toLocaleLowerCase("tr"))) {
            update("note_templates", [...list, tpl]);
        }
        setNewTemplate("");
    };

    const moveTemplate = (i, dir) => {
        const list = [...(form.note_templates || [])];
        const j = i + dir;
        if (j < 0 || j >= list.length) return;
        [list[i], list[j]] = [list[j], list[i]];
        update("note_templates", list);
    };

    const handleSave = () => {
        const msg = validate();
        if (msg) { setError(msg); setSaved(false); return; }
        setError(null);
        requestSaveWarningSettings({
            responseHours: form.response_hours,
            recipients: form.recipients,
            keywords: form.keywords,
            keywordRecipients: form.keyword_recipients,
            autoResolve: form.auto_resolve,
            reminderEnabled: form.reminder_enabled,
            reminderHours: form.reminder_hours,
            escalationEnabled: form.escalation_enabled,
            escalationHours: form.escalation_hours,
            noteTemplates: form.note_templates || [],
        })
            .then((data) => {
                setForm(data.settings);
                setSaved(true);
            })
            .catch((error) => {
                console.error(error.status, error.message);
                openErrorModal(error);
            });
    };

    const section = "flex flex-col gap-2 border-b border-gray-200 dark:border-gray-600 pb-4";
    const numberInput = "input input-sm input-bordered w-24 bg-white dark:bg-gray-700";
    const [level1, level2] = [form.escalation_hours[0] ?? 48, form.escalation_hours[1] ?? 72];

    return (
        <div className="flex flex-col w-full bg-white dark:bg-gray-700 rounded-xl shadow-lg p-5 gap-4">
            <div className={section}>
                <h2 className="font-semibold">{t("warning.settings_response_title")}</h2>
                <p className="text-sm opacity-80">{t("warning.settings_response_help")}</p>
                <label className="flex items-center gap-2 text-sm">
                    <input type="number" min={1} max={720} step={1} className={numberInput} value={form.response_hours} onChange={(e) => update("response_hours", e.target.value)} />
                    {t("warning.hours")}
                </label>
                {override !== null && (
                    <p className="text-xs text-orange-600">
                        {t("warning.settings_overridden", { hours: override, minutes: Math.round(override * 60), limit: form.response_hours })}
                    </p>
                )}
            </div>

            <div className={section}>
                <h2 className="font-semibold">{t("warning.settings_reminder_title")}</h2>
                <Toggle id="ws-reminder" checked={form.reminder_enabled} onChange={(v) => update("reminder_enabled", v)} label={t("warning.settings_reminder_toggle")} />
                <p className="text-sm opacity-80">{t("warning.settings_reminder_help")}</p>
                <label className="flex items-center gap-2 text-sm">
                    <input type="number" min={1} max={719} step={1} className={numberInput} disabled={!form.reminder_enabled} value={form.reminder_hours} onChange={(e) => update("reminder_hours", e.target.value)} />
                    {t("warning.hours")}
                </label>
                {form.reminder_enabled && <p className="text-xs opacity-60">{t("warning.hint_reminder_max", { limit: form.response_hours })}</p>}
            </div>

            <div className={section}>
                <h2 className="font-semibold">{t("warning.settings_escalation_title")}</h2>
                <Toggle id="ws-escalation" checked={form.escalation_enabled} onChange={(v) => update("escalation_enabled", v)} label={t("warning.settings_escalation_toggle")} />
                <p className="text-sm opacity-80">{t("warning.settings_escalation_help")}</p>
                <div className="flex flex-wrap items-center gap-2 text-sm">
                    <input type="number" min={1} max={720} step={1} className={numberInput} disabled={!form.escalation_enabled} value={level1} onChange={(e) => update("escalation_hours", [e.target.value, level2])} />
                    {t("warning.hours")} ·
                    <input type="number" min={1} max={720} step={1} className={numberInput} disabled={!form.escalation_enabled} value={level2} onChange={(e) => update("escalation_hours", [level1, e.target.value])} />
                    {t("warning.hours")}
                </div>
                {form.escalation_enabled && <p className="text-xs opacity-60">{t("warning.hint_escalation_min", { limit: form.response_hours })}</p>}
            </div>

            <div className={section}>
                <h2 className="font-semibold">{t("warning.settings_auto_resolve_title")}</h2>
                <Toggle id="ws-auto-resolve" checked={form.auto_resolve} onChange={(v) => update("auto_resolve", v)} label={t("warning.settings_auto_resolve_toggle")} />
                <p className="text-sm opacity-80">{t("warning.settings_auto_resolve_help")}</p>
            </div>

            <div className={section}>
                <h2 className="font-semibold">{t("warning.settings_recipients_title")}</h2>
                <p className="text-sm opacity-80">{t("warning.settings_recipients_help")}</p>
                <UserChecklist users={users} selected={form.recipients} onChange={(v) => update("recipients", v)} />
            </div>

            <div className={section}>
                <h2 className="font-semibold">{t("warning.settings_keywords_title")}</h2>
                <p className="text-sm opacity-80">{t("warning.settings_keywords_help")}</p>
                <div className="flex flex-wrap gap-2">
                    {form.keywords.map((kw) => (
                        <span key={kw} className="badge badge-lg gap-1 bg-rose-100 dark:bg-rose-900 border-none">
                            {kw}
                            <button onClick={() => update("keywords", form.keywords.filter((k) => k !== kw))}><MdClose /></button>
                        </span>
                    ))}
                </div>
                <div className="flex gap-2">
                    <input
                        className="input input-sm input-bordered w-72 bg-white dark:bg-gray-700"
                        value={newKeyword}
                        maxLength={100}
                        placeholder={t("warning.keyword_placeholder")}
                        onChange={(e) => setNewKeyword(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && addKeyword()}
                    />
                    <button className="btn btn-sm" onClick={addKeyword}>{t("warning.add")}</button>
                </div>
                <h3 className="text-sm font-medium mt-2">{t("warning.settings_keyword_recipients_title")}</h3>
                <UserChecklist users={users} selected={form.keyword_recipients} onChange={(v) => update("keyword_recipients", v)} />
            </div>

            <div className={section}>
                <h2 className="font-semibold">{t("warning.settings_templates_title")}</h2>
                <p className="text-sm opacity-80">{t("warning.settings_templates_help")}</p>
                <div className="flex flex-col gap-1">
                    {(form.note_templates || []).map((tpl, i) => (
                        <div key={i} className="flex items-center gap-1">
                            <span className="flex-1 text-sm bg-gray-100 dark:bg-gray-800 rounded px-2 py-1">{tpl}</span>
                            <button className="btn btn-xs btn-ghost px-1" disabled={i === 0} onClick={() => moveTemplate(i, -1)} aria-label={t("warning.move_up")}><MdKeyboardArrowUp size={16} /></button>
                            <button className="btn btn-xs btn-ghost px-1" disabled={i === (form.note_templates || []).length - 1} onClick={() => moveTemplate(i, 1)} aria-label={t("warning.move_down")}><MdKeyboardArrowDown size={16} /></button>
                            <button className="btn btn-xs btn-ghost px-1 text-red-600" onClick={() => update("note_templates", (form.note_templates || []).filter((_, k) => k !== i))} aria-label={t("warning.remove")}><MdClose size={16} /></button>
                        </div>
                    ))}
                    {(form.note_templates || []).length === 0 && <span className="text-sm italic opacity-60">{t("warning.settings_templates_empty")}</span>}
                </div>
                <div className="flex gap-2">
                    <input
                        className="input input-sm input-bordered flex-1 max-w-md bg-white dark:bg-gray-700"
                        value={newTemplate}
                        maxLength={500}
                        placeholder={t("warning.template_placeholder")}
                        onChange={(e) => setNewTemplate(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && addTemplate()}
                    />
                    <button className="btn btn-sm" onClick={addTemplate}>{t("warning.add")}</button>
                </div>
            </div>

            <div className="flex items-center justify-end gap-3">
                {error && <span className="text-sm text-red-600 dark:text-red-400 mr-auto text-left">{error}</span>}
                {saved && <span className="text-sm text-green-700 dark:text-green-400">{t("warning.saved")}</span>}
                <button className="btn btn-sm bg-primary text-white border-none hover:bg-sky-700" onClick={handleSave}>{t("warning.save")}</button>
            </div>
        </div>
    );
};
